import './assets/scss/styles.scss';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import React, { useEffect, useState } from 'react';

import { I18nextProvider } from 'react-i18next';
import i18n, {
  applyLanguage,
  getPreferredLanguage,
  LANGUAGE_CHANGE_EVENT
} from './i18n';

import apiClient from './utils/apiClient';

import Login from './components/pages/login/login';
import Welcome from './components/pages/welcome/welcome';
import PasswordRecovery from './components/pages/login/password-recovery/password-recovery';
import PasswordReset from './components/pages/login/password-reset/password-reset';
import Test from './components/pages/test/test';
import AutoLogin from './components/pages/auto-login/auto-login.js';

import MainHac from './components/pages/role-hac/main/main';
import HacExperience from './components/pages/role-hac/main/experience';
import HacExperienceCatalog from './components/pages/role-hac/main/catalog';
import HacProduct from './components/pages/role-hac/main/product';
import HacProductCatalog from './components/pages/role-hac/main/catalog-product';
import HacBasicInfo from './components/pages/role-hac/profile/basic-info';
import HacVehicles from './components/pages/role-hac/profile/vehicles';
import HacEditVehicle from './components/pages/role-hac/profile/edit-vehicle';
import HacPreferences from './components/pages/role-hac/preferences/preferences';
import HacLegalPrint from './components/pages/role-hac/legal/print';
import HacLegalPrivacy from './components/pages/role-hac/legal/privacy';
import HacLegalTerms from './components/pages/role-hac/legal/terms';
import HacNews from './components/pages/role-hac/news/news';
import HacSurvey from './components/pages/role-hac/survey/survey';
import SurveyThankYou from './components/pages/role-hac/survey/thank-you';

import MainSuperAdmin from './components/pages/role-super-admin/main/main';

import AdminMain from './components/pages/role-admin/main/admin-main';
import AdminLibrary from './components/pages/role-admin/main/library';
import RetailerDetails from './components/pages/role-admin/retailer-details/retailer-details';
import NominateAdmin from './components/pages/role-admin/nominate/nominate.js';
import NominatingRetailer from './components/pages/role-admin/nominating-retailer/nominating-retailer';
import AssignedPmas from './components/pages/role-admin/asigned-pmas/assigned-pmas';
import AdminSharedNotes from './components/pages/role-admin/shared-notes/shared-notes';
import AdminSurveys from './components/pages/role-admin/surveys/surveys';
import AdminSurvey from './components/pages/role-admin/surveys/survey';
import NewRetailer from './components/pages/role-admin/new-retailer/new-retailer';
import NewSaleperson from './components/pages/role-admin/new-saleperson/new-saleperson';
import NewPma from './components/pages/role-admin/new-pma/new-pma';
import NewAdmin from './components/pages/role-admin/new-admin/new-admin';
import NewTranslationUser from './components/pages/role-admin/new-translation-user/new-translation-user';
import NewRetailerGM from './components/pages/role-admin/new-retailerGM/new-retailerGM';
import DeleteUser from './components/pages/role-admin/delete-user/delete-user';
import EditRetailer from './components/pages/role-admin/edit-retailer/edit-retailer';
import EditRetailerGM from './components/pages/role-admin/edit-retailerGM/edit-retailerGM';
import EditSaleperson from './components/pages/role-admin/edit-saleperson/edit-saleperson';
import EditPma from './components/pages/role-admin/edit-pma/edit-pma';
import EditAdmin from './components/pages/role-admin/edit-admin/edit-admin';

import RetailerMain from './components/pages/role-retailer/main/retailer-main';
import NominateHac from './components/pages/role-retailer/nominate-hac/nominate-hac';
import RetailerExperienceCatalog from './components/pages/role-retailer/main/catalog';
import RetailerExperience from './components/pages/role-retailer/main/experience';
import RetailerSharedNotes from './components/pages/role-retailer/shared-notes/shared-notes';

import MainShippingAdmin from './components/pages/role-shipping-admin/main/main';

import PmaMain from './components/pages/role-pma/main/pma-main';
import AccountSettings from './components/organisms/common/settings/account-settings';

import Vehicles from './components/pages/common/vehicles/vehicles';
import EditHacVehicle from './components/pages/common/vehicles/edit-vehicle';
import HacProfile from './components/pages/common/hac-profile/hac-profile.js';
import ListHacPreferences from './components/pages/common/preferences/preferences';
import RetailerInfo from './components/pages/role-pma/retailer-info/retailer-info';
import PmaInfo from './components/pages/role-pma/pma-info/pma-info';
import Unenroll from './components/pages/role-pma/unenroll/unenroll';
import PmaSharedNotes from './components/pages/role-pma/shared-notes/shared-notes';
import PmaSurveys from './components/pages/role-pma/surveys/surveys';
import PmaSurvey from './components/pages/role-pma/surveys/survey';
import ActivityLog from './components/pages/common/activity-log/activity-log';
import DeleteUserPma from './components/pages/role-pma/delete-user/delete-user';
import NewPmaPmaGM from './components/pages/role-pma/new-pma/new-pma';
import PmaEditPma from './components/pages/role-pma/edit-pma/edit-pma';
import PmaExperience from './components/pages/role-pma/main/experience';
import PmaExperienceCatalog from './components/pages/role-pma/main/catalog';

import PrivateRoutes from './utils/private-routes';

import Error404 from './components/pages/error/error404/error404';
import ErrorGeneric from './components/pages/error/generic/error-generic';
import ExpiredInvitation from './components/pages/error/expired-invitation/expired-invitation';
import ErrorMaintenance from './components/pages/error/maintenance/error-maintenance';
import ModalSessionExpired from './components/organisms/modal-session/modal-session.js';

const MAINTENANCE_MODE = false;

function App() {
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [i18nReady, setI18nReady] = useState(false);

  const handleSessionExpired = () => setIsSessionExpired(true);

  const handleCloseModal = () => {
    setIsSessionExpired(false);
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('preferred_language');
    sessionStorage.removeItem('loggedUser');
  };

  useEffect(() => {
    let mounted = true;

    const initLanguage = async () => {
      const preferredLanguage = getPreferredLanguage();
      await applyLanguage(preferredLanguage);

      if (mounted) {
        setI18nReady(true);
      }
    };

    const onLanguageChanged = () => {
      if (mounted) {
        setI18nReady(true);
      }
    };

    initLanguage();
    window.addEventListener(LANGUAGE_CHANGE_EVENT, onLanguageChanged);

    return () => {
      mounted = false;
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, onLanguageChanged);
    };
  }, []);

  useEffect(() => {
    const interceptorId = apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (
          error.response &&
          error.response.status === 401 &&
          !window.location.href.includes('welcome')
        ) {
          handleSessionExpired();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      apiClient.interceptors.response.eject(interceptorId);
    };
  }, []);

  if (!i18nReady) {
    return null;
  }

  return (
    <I18nextProvider i18n={i18n}>
      <Router>
        <Routes>
          {MAINTENANCE_MODE ? (
            <Route path="*" element={<ErrorMaintenance />} />
          ) : (
            <>
              <Route element={<PrivateRoutes />}>
                <Route element={<RetailerMain />} path="/retailer/main" />
                <Route element={<RetailerMain />} path="/retailer/main/active" />
                <Route element={<RetailerMain />} path="/retailer/main/nominated" />
                <Route element={<RetailerMain />} path="/retailer/main/exited" />
                <Route element={<RetailerExperienceCatalog />} path="/retailer/main/catalog" />
                <Route element={<RetailerExperience />} path="/retailer/experience" />
                <Route element={<NominateHac />} path="/retailer/nominate-hac" />
                <Route element={<HacProfile />} path="/common/personal-details/:hacId" />
                <Route element={<AccountSettings />} path="/retailer/settings" />
                <Route element={<Vehicles />} path="/common/vehicles/:hacId" />
                <Route element={<EditHacVehicle />} path="/common/vehicles/new-vehicle" />
                <Route element={<EditHacVehicle />} path="/common/vehicles/edit-vehicle" />
                <Route element={<ListHacPreferences />} path="/common/preferences/:hacId" />
                <Route element={<RetailerSharedNotes />} path="/retailer/shared-notes/:hacId" />

                <Route element={<MainHac />} path="/hac/main" />
                <Route element={<HacExperience />} path="/hac/experience" />
                <Route element={<HacExperienceCatalog />} path="/hac/catalog" />
                <Route element={<HacProduct />} path="/hac/limited-edition" />
                <Route element={<HacProductCatalog />} path="/hac/catalog-limited-edition" />
                <Route element={<HacBasicInfo />} path="/hac/profile/basic-info" />
                <Route element={<HacVehicles />} path="/hac/profile/vehicles" />
                <Route element={<HacEditVehicle />} path="/hac/profile/new-vehicle" />
                <Route element={<HacEditVehicle />} path="/hac/profile/edit-vehicle" />
                <Route element={<HacPreferences />} path="/hac/preferences" />
                <Route element={<AccountSettings />} path="/hac/settings" />
                <Route element={<HacLegalPrint />} path="/hac/legal/print" />
                <Route element={<HacLegalPrivacy />} path="/hac/legal/privacy" />
                <Route element={<HacLegalTerms />} path="/hac/legal/terms" />
                <Route element={<HacNews />} path="/hac/news" />
                <Route element={<HacSurvey />} path="/hac/survey" />
                <Route element={<SurveyThankYou />} path="/hac/survey/thank-you" />

                <Route element={<PmaMain />} path="/pma/calendar" />
                <Route element={<PmaMain />} path="/pma/main" />
                <Route element={<PmaMain />} path="/pma/main/hac" />
                <Route element={<PmaMain />} path="/pma/main/pma" />
                <Route element={<PmaMain />} path="/pma/main/retailers" />
                <Route element={<AccountSettings />} path="/pma/settings" />
                <Route element={<Vehicles />} path="/common/vehicles/:hacId" />
                <Route element={<EditHacVehicle />} path="/common/vehicles/new-vehicle" />
                <Route element={<EditHacVehicle />} path="/common/vehicles/edit-vehicle" />
                <Route element={<ListHacPreferences />} path="/common/preferences/:hacId" />
                <Route element={<RetailerInfo />} path="/pma/retailer-info/:hacId" />
                <Route element={<PmaInfo />} path="/pma/pma-info/:hacId" />
                <Route element={<HacProfile />} path="/common/personal-details/:hacId" />
                <Route element={<Unenroll />} path="/pma/unenroll/:hacId" />
                <Route element={<PmaSharedNotes />} path="/pma/shared-notes/:hacId" />
                <Route element={<PmaSurveys />} path="/pma/surveys/:hacId" />
                <Route element={<PmaSurvey />} path="pma/survey" />
                <Route element={<DeleteUserPma />} path="/pma/delete-user" />
                <Route element={<NewPmaPmaGM />} path="/pma/new-pma" />
                <Route element={<PmaEditPma />} path="/pma/edit-pma" />
                <Route element={<ActivityLog />} path="/common/activity-log/:hacId" />
                <Route element={<PmaExperience />} path="/pma/experience" />
                <Route element={<PmaExperienceCatalog />} path="/pma/catalog" />

                <Route element={<AdminMain />} path="/admin/main" />
                <Route element={<AdminMain />} path="/admin/main/hac" />
                <Route element={<AdminMain />} path="/admin/main/pma" />
                <Route element={<AdminMain />} path="/admin/main/retailer" />
                <Route element={<AdminMain />} path="/admin/main/admin" />
                <Route element={<AdminMain />} path="/admin/main/translator" />
                <Route element={<AdminLibrary />} path="/admin/main/library" />
                <Route element={<RetailerDetails />} path="/admin/retailer-info/:retailerId" />
                <Route element={<NominatingRetailer />} path="/admin/nominating-retailer/:hacId" />
                <Route element={<AssignedPmas />} path="/admin/assigned-pmas/:hacId" />
                <Route element={<AccountSettings />} path="/admin/settings" />
                <Route element={<AccountSettings />} path="/translator/settings" />
                <Route element={<NominateAdmin />} path="/admin/nominate" />
                <Route element={<AdminSharedNotes />} path="/admin/shared-notes/:hacId" />
                <Route element={<AdminSurveys />} path="/admin/surveys/:hacId" />
                <Route element={<AdminSurvey />} path="/admin/survey" />
                <Route element={<Vehicles />} path="/common/vehicles/:hacId" />
                <Route element={<EditHacVehicle />} path="/common/vehicles/new-vehicle" />
                <Route element={<HacProfile />} path="/common/personal-details/:hacId" />
                <Route element={<EditHacVehicle />} path="/common/vehicles/edit-vehicle" />
                <Route element={<ListHacPreferences />} path="/common/preferences/:hacId" />
                <Route element={<NewRetailer />} path="/admin/new-retailer" />
                <Route element={<NewSaleperson />} path="/admin/new-saleperson/:retailerId" />
                <Route element={<NewPma />} path="/admin/new-pma" />
                <Route element={<NewAdmin />} path="/admin/new-admin" />
                <Route element={<NewTranslationUser />} path="/admin/new-translation-user" />
                <Route
                  element={<NewTranslationUser />}
                  path="/admin/edit-translation-user/:translationUserRole/:translationUserId"
                />
                <Route element={<NewRetailerGM />} path="/admin/new-retailerGM/:retailerId" />
                <Route element={<DeleteUser />} path="/admin/delete-user" />
                <Route element={<EditRetailer />} path="/admin/edit-retailer/:retailerId" />
                <Route element={<EditRetailerGM />} path="/admin/edit-retailerGM/:retailerId" />
                <Route element={<EditSaleperson />} path="/admin/edit-saleperson/:retailerId" />
                <Route element={<EditPma />} path="/admin/edit-pma" />
                <Route element={<EditAdmin />} path="/admin/edit-admin" />
                <Route element={<ActivityLog />} path="/common/activity-log/:hacId" />

                <Route element={<MainSuperAdmin />} path="/super-admin/main" />
                <Route element={<AccountSettings />} path="/super-admin/settings" />

                <Route element={<MainShippingAdmin />} path="/shipping-admin/main" />
                <Route element={<AccountSettings />} path="/shipping-admin/settings" />
              </Route>

              <Route element={<Login />} path="/" />
              <Route element={<Login />} path="/login" />
              <Route element={<Login />} path="/login/:logout?" />
              <Route element={<Welcome />} path="/welcome/:token" />
              <Route element={<PasswordRecovery />} path="/password-recovery" />
              <Route element={<PasswordReset />} path="/password-reset/:token" />
              <Route element={<Test />} path="/test" />
              <Route element={<AutoLogin />} path="/auto-login" />
              <Route element={<ErrorGeneric />} path="/error" />
              <Route element={<ExpiredInvitation />} path="/expired" />
              <Route element={<ExpiredInvitation />} path="/expired/:token" />
              <Route element={<ErrorMaintenance />} path="/maintenance" />
              <Route element={<Error404 />} path="*" />
            </>
          )}
        </Routes>

        <ModalSessionExpired open={isSessionExpired} handleClose={handleCloseModal} />
      </Router>
    </I18nextProvider>
  );
}

export default App;
